import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView } from '@form/cer-form-view';

import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerUiIcons } from '@ui/cer-ui-icons';
import { ExchangeMsgClient } from 'src/app/api';
import { PromptInput } from '@cer/cer-ui/cer-ui-prompt.service';
import { CerFormContext } from '@cer/cer-form/cer-form-context';
import { getDate } from 'date-fns/esm';

const uiCmdExchangeGetLatest: UiCommand = { id: 'ExchangeGetLatest', text: 'Hent seneste', iconCss: CerUiIcons.import, tooltipText: 'Kør integration og hent seneste ændringer', target: ".e-content", needSelection: true };
const uiCmdExchangeGetLatestSince: UiCommand = { id: 'ExchangeGetLatestSince', text: 'Hent seneste siden..', iconCss: CerUiIcons.import, tooltipText: 'Kør integration og hent seneste ændringer siden angivet dato', target: ".e-content", needSelection: true };
const uiCmdExchangeGetAll: UiCommand = { id: 'uiCmdExchangeGetAll', text: 'Hent alle', iconCss: CerUiIcons.import, tooltipText: 'Kør integration og hent alle data', target: ".e-content", needSelection: true };
const uiCmdExchangeGetRows: UiCommand = { id: 'uiCmdExchangeGetRows', text: 'Hent angivne..', iconCss: CerUiIcons.import, tooltipText: 'Kør integration og hent alle data', target: ".e-content", needSelection: true };

export class ExchangeTypeSubscriberForm extends CerFormAdapter {

  public override viewSetInit(viewSet: CerFormViewSet): void {
    const subscriberView = ExchangeTypeSubscriberForm.subscriberViewGet(this.context);
    viewSet.viewAdd(subscriberView);
  }

  public static subscriberViewGet(context: CerFormContext, parentPrimaryKey: string = null, parentForeignKey: string = null): CerFormView {
    return new CerFormView({
      viewMetadata: {
        dataApiName: 'ExchangeTypeSubscriber',
        name: 'data-udveksling-abonnementer', text: 'Abonnementer',
        primaryKey: 'id', parentPrimaryKey: parentPrimaryKey, parentForeignKey: parentForeignKey,
        baseFields: ['id', 'history'],
        navigations: [
          { link: 'nav', foreignKey: { entity: 'DataJobLog', column: 'jobId', parentColumn: 'dataFeedJobId' } },
          { link: 'fk', foreignKey: { entity: 'ExchangeParty', column: 'id', parentColumn: 'exchangeParty' } },
          { link: 'fk', foreignKey: { entity: 'DataJob', column: 'id', parentColumn: 'dataFeedJobId' } }
        ]
      },
      fieldMetadata: [
        { name: 'exchangeType', text: 'Integration', foreignKeyTableName: 'ExchangeType' },
        { name: 'exchangeParty', text: 'Modpart', foreignKeyTableName: 'ExchangeParty' },
        { name: 'dataFeedActive', text: 'Aktiv indgående', format: 'CheckBox', defaultValue: true },
        { name: 'dataFeedName', text: 'Job indgående' },
        { name: 'dataFeedJobId', text: 'Datajob indgående', foreignKeyTableName: 'DataJob', foreignKeyField: 'id', foreignKeyValue: 'name' },
        { name: 'dataFeedOptions', text: 'Indstillinger indgående' },
        { name: 'dataPublishActive', text: 'Aktiv udgående', format: 'CheckBox', defaultValue: true },
        { name: 'dataPublishName', text: 'Job udgående' },
        { name: 'dataPublishJobId', text: 'Datajob udgående', foreignKeyTableName: 'DataJob', foreignKeyField: 'id', foreignKeyValue: 'name' },
        { name: 'dataPublishOptions', text: 'Indstillinger udgående' }
      ],
      fieldGroupMetadata: [
        { idx: 0, name: 'identification', text: 'Identifikation', fields: ['id','exchangeType','exchangeParty']},
        { idx: 1, name: 'dataFeed', text: 'Indgående data', fields: ['dataFeedActive','dataFeedName','dataFeedJobId','dataFeedOptions']},
        { idx: 2, name: 'dataPublish', text: 'Udgående data', fields: ['dataPublishActive','dataPublishName','dataPublishJobId','dataPublishOptions']}
      ],
      contextMenuCommands: [ uiCmdExchangeGetRows,uiCmdExchangeGetLatest, uiCmdExchangeGetLatestSince, uiCmdExchangeGetAll],
      onCommandEvent: (e => ExchangeTypeSubscriberForm.onSubscriberCommand(e, context)),
    });

  }

  // Commands
  public static onSubscriberCommand(event: UiCommandEvent, context: CerFormContext) {
    switch (event.commandId) {
      case uiCmdExchangeGetLatest.id:
      case uiCmdExchangeGetLatestSince.id:
      case uiCmdExchangeGetAll.id:
      case uiCmdExchangeGetRows.id:
        if (event.rowData.dataFeedActive === true) {
          ExchangeTypeSubscriberForm.subscriptionGet(event.command.id, event.rowData?.id, event.rowData?.dataFeedName, context);
        }
        else {
          context.ui.error("Ingen aktivt abonnement er markeret");
        }
        break;
    }
  }

  public static async subscriptionGet(commandId: string, subscriberId: number, name: string, context: CerFormContext) {
    const client: ExchangeMsgClient = context.injector.get(ExchangeMsgClient);
    switch (commandId) {
      case uiCmdExchangeGetLatest.id:
        context.ui.operationBegin(`Henter seneste data fra '${name}'`);
        client.getLatestChangesFromSubscription(subscriberId, null).subscribe({
          next: (ok) => context.ui.operationEnd("Fuldført"),
          error: (error) => context.ui.operationEndError(error)
        });
        break;
      case uiCmdExchangeGetLatestSince.id:
        const previousDate = await ExchangeTypeSubscriberForm.promptPreviousDate(uiCmdExchangeGetLatestSince.text, context);
        context.ui.operationBegin(`Henter seneste data fra '${name}' siden ${context.ui.formatDateTime(previousDate)}`);
        client.getLatestChangesFromSubscription(subscriberId, previousDate).subscribe({
          next: (ok) => context.ui.operationEnd("Fuldført"),
          error: (error) => context.ui.operationEndError(error)
        });
        break;
      case uiCmdExchangeGetAll.id:
        context.ui.operationBegin(`Henter alle data fra '${name}'`);
        client.getAllForSubscription(subscriberId).subscribe({
          next: (ok) => context.ui.operationEnd("Fuldført"),
          error: (error) => context.ui.operationEndError(error)
        });
        break;
      case uiCmdExchangeGetRows.id:
        const rowsId: string[] = await ExchangeTypeSubscriberForm.promptRowsId(uiCmdExchangeGetRows.text, context);
        if (rowsId.length > 0) {
          context.ui.operationBegin(`Henter post ${rowsId.join(",")} fra '${name}'`);
          client.getRowsByIdsForSubscription(subscriberId, rowsId).subscribe({
            next: (ok) => context.ui.operationEnd("Fuldført"),
            error: (error) => context.ui.operationEndError(error)
          });
        }
        break;
    }
  }

  private static async promptPreviousDate(title: string, context: CerFormContext): Promise<Date> {
    const bodyHeader = "Angiv fra dato for ændringer";
    const inputId = 'previousDate';
    const previousDate = new Date();
    var inputs: PromptInput[] = [
      { label: "Fra dato", id: inputId, type: 'DateTime', placeholder: "Angiv fra dato", valueDate: previousDate, element: null
 },
    ];
    const result = await context.ui.promptInputs(bodyHeader, title, inputs);
    if (result.action == 'ok') {
      return result.inputs.find(i => i.id == inputId).valueDate;
    }
    return null;
  }

  private static async promptRowsId(title: string, context: CerFormContext): Promise<string[]> {
    const bodyHeader = "Angiv nøgle på post der skal hentes fra datafeed";
    const inputId = 'pk';
    const inputs: PromptInput[] = [
      { label: "Nøgle (ISBN ved titel)", id: inputId, placeholder: "Angiv nøgle/ISBN", value: '', element: null },
    ];
    const result = await context.ui.promptInputs(bodyHeader, title, inputs);
    if (result.action == 'ok') {
      const value: string = result.inputs.find(i => i.id == inputId).value;
      if (value?.length > 0) {
        return [value];
      }
    }
    return null;
  }
}
